/**
 * Print sessions — the in-memory model behind a print workspace tab.
 *
 * A print form is not restorable from its URL: it carries a filled, possibly
 * hand-edited spreadsheet model that exists only in this browser session. So a
 * print tab keeps its descriptor here and takes a synthetic path (`/print/<id>`)
 * purely as a tab identity, and `tabSession` never persists it — reloading must
 * not resurrect a tab whose content is gone.
 */
import type { PrintWindowDescriptor } from './openPrintWindow'

const sessions = new Map<string, PrintWindowDescriptor>()
/** Bumped when a reused session's descriptor is replaced (re-print refresh). */
const revisions = new Map<string, number>()
const listeners = new Set<() => void>()

/** Monotonic, so ids stay readable and tests stay deterministic. */
let nextId = 1

function notify(): void {
  listeners.forEach((l) => l())
}

/**
 * Session identity for re-print → reuse tab.
 * Deliberately ignores `model`/`title` (object identity / payload size): a
 * model-only open still keys on layoutNodeId + instanceId (plus connection/table).
 * On reuse the stored descriptor IS replaced so the tab shows the new fill.
 */
const descriptorKey = (d: PrintWindowDescriptor) =>
  [d.connectionId, d.tableName, d.instanceId, d.layoutNodeId].join('|')

export function printTabPath(sessionId: string): string {
  return `/print/${encodeURIComponent(sessionId)}`
}

/**
 * Register a print session, or reuse the one already showing this exact form —
 * re-printing the same document activates its tab instead of opening a second.
 * On reuse, replaces the stored descriptor (new model/title) and bumps revision
 * so the tab body remounts without changing the path key.
 */
export function openPrintSession(descriptor: PrintWindowDescriptor): {
  sessionId: string
  path: string
  existed: boolean
} {
  const key = descriptorKey(descriptor)
  const open = Array.from(sessions.entries()).find(([, d]) => descriptorKey(d) === key)
  if (open) {
    const existingId = open[0]
    sessions.set(existingId, descriptor)
    revisions.set(existingId, (revisions.get(existingId) ?? 0) + 1)
    notify()
    return { sessionId: existingId, path: printTabPath(existingId), existed: true }
  }
  const sessionId = `pw-${nextId++}`
  sessions.set(sessionId, descriptor)
  revisions.set(sessionId, 0)
  notify()
  return { sessionId, path: printTabPath(sessionId), existed: false }
}

export function getPrintSession(sessionId: string): PrintWindowDescriptor | null {
  return sessions.get(sessionId) ?? null
}

/** Revision for a session — increments when an open reuses/replaces its descriptor. */
export function getPrintSessionRevision(sessionId: string): number {
  return revisions.get(sessionId) ?? 0
}

/** Subscribe to session registry changes (descriptor replace / dispose). */
export function subscribePrintSessions(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange)
  return () => {
    listeners.delete(onStoreChange)
  }
}

export function disposePrintSession(sessionId: string): void {
  sessions.delete(sessionId)
  revisions.delete(sessionId)
  notify()
}

/** Drops the session behind a `/print/<id>` tab path; ignores any other path. */
export function disposePrintSessionByPath(path: string): void {
  const id = printSessionIdFromPath(path)
  if (id) disposePrintSession(id)
}

export function printSessionIdFromPath(path: string): string | null {
  const pathname = path.split('?')[0]
  const m = pathname.match(/^\/print\/([^/]+)$/)
  return m ? decodeURIComponent(m[1]) : null
}

/** True while at least one print form is open (gates the record form's ⌘S / ⌘⏎). */
export function hasOpenPrintSessions(): boolean {
  return sessions.size > 0
}

export function _resetPrintSessionsForTests(): void {
  sessions.clear()
  revisions.clear()
  listeners.clear()
  nextId = 1
}
